import { DOCUMENT, NgTemplateOutlet } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, ElementRef, HostListener, inject, signal, viewChild } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router, RouterLink, RouterLinkActive, RouterOutlet } from '@angular/router';
import { initials, isFresh, telHref } from 'app/common/format/vz-format';
import { VzPhonePipe, VzWhenPipe } from 'app/common/format/vz-format.pipes';
import { TPipe } from 'app/common/i18n/t.pipe';
import { VzGlobalSearchComponent } from 'app/component/vz/vz-global-search.component';
import { VzIconComponent } from 'app/component/vz/vz-icon.component';
import { AuthService } from 'app/core/auth/auth.service';
import { StorageService } from 'app/core/storage/storage.service';
import { MAX_AGE_MS, PanelStore } from 'app/core/panel/panel-store.service';
import { VZ_FEATURES } from 'app/core/panel/vz-features';
import { isEndingSoon } from 'app/core/panel/panel.calc';
import { eventLink, eventQuery } from 'app/core/panel/event-link';
import { filter, fromEvent, interval } from 'rxjs';
import { VzOwnerComponent } from 'app/component/vz/vz-owner.component';

type Popover = 'account' | 'bell' | null;

/** Dzwonek: 5 najnowszych zdarzeń — te same co oś na stronie głównej (bez spraw zamkniętych). */
const BELL_SIZE = 5;

/** Odświeżanie w tle (Issue 36): sprawdzenie co 10 s, odświeżenie, gdy dane mają ponad 50 s — w sumie ≤ 60 s. */
const REFRESH_TICK_MS = 10_000;
/** Powrót do karty: odświeżenie, jeśli ostatnie było ponad 5 s temu. */
const RETURN_REFRESH_MS = 5_000;

/**
 * Powłoka panelu klienta (Issue 18): jasna kolumna 264 px z logo, kontem i nawigacją, karta opiekuna,
 * pasek 64 px (58 px < 980 px), poniżej 980 px kolumna jako wysuwany panel ze scrimem (Esc zamyka).
 *
 * Bez OnPush: router-outlet powłoki hostuje też ekrany sprzed etapu 2 (listy i strony pojazdów i polis),
 * które odświeżają się zwykłą detekcją zmian — pod rodzicem OnPush dane z API nie trafiały do widoku.
 * Do przywrócenia, gdy etap 3 zastąpi te ekrany.
 */
@Component({
    selector: 'vazuvi-layout',
    standalone: true,
    imports: [VzOwnerComponent, NgTemplateOutlet, RouterOutlet, RouterLink, RouterLinkActive, VzGlobalSearchComponent, VzIconComponent, TPipe, VzPhonePipe, VzWhenPipe],
    templateUrl: './vazuvi.component.html',
    styleUrl: './vazuvi.component.scss',
    changeDetection: ChangeDetectionStrategy.Default,
})
export class VazuviLayoutComponent {
    private readonly store = inject(PanelStore);
    private readonly storage = inject(StorageService);
    private readonly auth = inject(AuthService);
    private readonly host = inject(ElementRef<HTMLElement>);

    protected readonly features = VZ_FEATURES;
    protected readonly navOpen = signal(false);
    protected readonly popover = signal<Popover>(null);
    protected readonly advisorOpen = signal(false);
    protected readonly clientMode = this.storage.isClientMode();

    private readonly hamburger = viewChild<ElementRef<HTMLButtonElement>>('hamburger');
    private readonly navClose = viewChild<ElementRef<HTMLButtonElement>>('navClose');

    protected readonly me = this.store.me;
    /** Firmy konta i filtr firmy (Issue 26). */
    protected readonly companies = this.store.companies;
    protected readonly multiCompany = this.store.multiCompany;
    protected readonly company = this.store.company;
    protected readonly relatedCount = computed(() => this.companies().filter((c) => c.relation === 'related').length);
    protected readonly clientName = computed(() => this.me()?.client.name ?? '');
    protected readonly clientInitials = computed(() => this.me()?.client.initials ?? '');
    protected readonly advisor = computed(() => this.me()?.advisor ?? null);
    protected readonly advisorInitials = computed(() => initials(this.advisor()?.name));
    protected readonly advisorTel = computed(() => telHref(this.advisor()?.phone));

    /** Licznik przy „Moje sprawy”: sprawy w toku z ruchem klienta. */
    /** „Twój ruch” w sprawach i zgłoszeniach (Issue 40 — jedna lista „Sprawy”). */
    protected readonly casesWaiting = computed(() =>
        this.features.cases
            ? (this.store.cases() ?? []).filter((c) => c.status === 'open' && !!c.clientAction).length +
              (this.store.tickets() ?? []).filter((t) => t.status === 'client').length
            : 0
    );

    private readonly events = computed(() =>
        this.features.events ? (this.store.events() ?? []).filter((e) => e.caseStatus !== 'closed') : []
    );

    protected readonly bellEvents = computed(() => this.events().slice(0, BELL_SIZE));
    protected readonly link = eventLink;
    protected readonly query = eventQuery;
    /** Kropka na dzwonku: zdarzenia z ostatnich 3 dni. */
    protected readonly freshEvents = computed(() => this.events().filter((e) => isFresh(e.at)).length);

    /** Kropka przy „Polisy”: aktywna polisa kończy się w ciągu 30 dni. */
    protected readonly policiesEnding = computed(() => {
        const now = new Date();

        return (this.store.policies() ?? []).filter((p) => isEndingSoon(p, now)).length;
    });

    private readonly router = inject(Router);

    constructor() {
        // Odświeżanie danych bez przeładowania (Issue 36): po powrocie do karty i co minutę, gdy karta jest na wierzchu
        const doc = inject(DOCUMENT);
        const visible = () => doc.visibilityState === 'visible';

        fromEvent(doc, 'visibilitychange')
            .pipe(filter(visible), takeUntilDestroyed())
            .subscribe(() => this.store.refreshIfStale(RETURN_REFRESH_MS));
        interval(REFRESH_TICK_MS)
            .pipe(filter(visible), takeUntilDestroyed())
            .subscribe(() => this.store.refreshIfStale(MAX_AGE_MS - REFRESH_TICK_MS));

        this.router.events
            .pipe(
                filter((e) => e instanceof NavigationEnd),
                takeUntilDestroyed()
            )
            .subscribe(() => {
                this.navOpen.set(false);
                this.popover.set(null);
                this.syncCompany();
            });
        this.syncCompany();
    }

    /** Filtr firmy: wybór zapisany w adresie (?firma=), żeby przetrwał przeładowanie i dał się wysłać linkiem. */
    pickCompany(clientId: number | null): void {
        this.store.selectCompany(clientId);
        this.setCompanyParam(this.store.company());
    }

    /** Adres z ?firma= ustawia filtr; przejście na stronę bez niego zachowuje wybraną firmę w adresie. */
    private syncCompany(): void {
        if (!this.store.multiCompany()) {
            return;
        }

        const param = this.router.routerState.snapshot.root.queryParamMap.get('firma');
        const selected = this.store.company();

        if (param !== null) {
            this.store.selectCompany(Number(param));

            if (this.store.company() === null) {
                // Firma spoza konta — bez parametru w adresie
                this.setCompanyParam(null);
            }
        } else if (selected !== null) {
            this.setCompanyParam(selected);
        }
    }

    /** ?firma= w bieżącym adresie, bez nowego wpisu w historii (pozostałe parametry, np. ?etap=, zostają). */
    private setCompanyParam(clientId: number | null): void {
        const tree = this.router.parseUrl(this.router.url);
        const { firma, ...rest } = tree.queryParams;

        tree.queryParams = clientId === null ? rest : { ...rest, firma: String(clientId) };

        if (String(firma ?? '') !== String(clientId ?? '')) {
            this.router.navigateByUrl(tree, { replaceUrl: true });
        }
    }

    openNav(): void {
        this.navOpen.set(true);
        this.popover.set(null);
        setTimeout(() => this.navClose()?.nativeElement.focus());
    }

    closeNav(): void {
        if (!this.navOpen()) {
            return;
        }

        this.navOpen.set(false);
        setTimeout(() => this.hamburger()?.nativeElement.focus());
    }

    togglePopover(name: Exclude<Popover, null>): void {
        this.popover.update((current) => (current === name ? null : name));
    }

    exitClientMode(): void {
        this.popover.set(null);
        this.store.reset();
        this.auth.exitClientMode();
    }

    @HostListener('document:keydown.escape')
    onEscape(): void {
        if (this.popover()) {
            this.popover.set(null);
        } else {
            this.closeNav();
        }
    }

    /** Klik poza otwartym panelem zamyka go (dzwonek, menu konta i wyszukiwarka wykluczają się). */
    @HostListener('document:click', ['$event'])
    onDocumentClick(event: MouseEvent): void {
        if (!this.popover()) {
            return;
        }

        const target = event.target as HTMLElement;
        const inside = target.closest('.vz-pop, [data-popover-trigger]');

        if (!inside || !this.host.nativeElement.contains(inside)) {
            this.popover.set(null);
        }
    }
}
